// Skills — a signal-backed scope dim over the backend `skill` signal_tags (O-2586).
//
// A skill is the durable annotation the backend emits per Skill-tool launch /
// SKILL.md read: namespace="skill", tag=<skill name>, value=<the injected body>.
// The dim is reached PROMPT → SignalTags exactly like `signal`/`cluster`, but it
// is TAG-valued within the FIXED `skill` namespace (DIM_INFO.skill carries
// `viaSignalTags` + `signalNamespace:"skill"`): the dim value is the skill name
// (st.tag), and the namespace gate keeps it to skill rows.
//
// Was a synthetic dim derived from PROMPT_RESOURCE_PATHS by an `%skills/<name>/%`
// match. That path-slug key space turned out disjoint from the Skill-tool names
// the signal carries, so the dim now reads the signal — and SkillDetailBody reads
// the same `skill` rows for the injected body + drift.
//
// Exports `skillAdapter` (DimAdapter) for registration in dimAdapters.js; the
// dispatch sites in analyticsClient/apiClient handle the `skill` viaSignalTags
// join generically (no skill-specific branch beyond the namespace gate).
import {
    buildPromptRootAndFilter,
    rangeToTimeRange,
    rowsToObjects,
    runQuery as runAnalyticsQuery,
    bucketSecondsForRange,
    rangeBoundsMs,
    densifyBuckets,
} from "./analyticsClient.js";
import { shapeDimensionEntries } from "./dimAdapters.js";
import { compareDimEntries } from "./dimSort.js";
import { toApiRange } from "./shell.js";

const NS = "skill";
const NS_FIELD = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE" };
const TAG_FIELD = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_TAG" };
const ST_EVENT_ID = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_EVENT_ID" };
const PROMPT_TS = { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" };
const SPARK_BUCKETS = 24;

// The id is the skill name, so it doubles as the display label.
export function labelForSkill(id) {
    return id ? String(id) : "";
}

// Breakdown / scope predicate: prompts whose signal_tags carry a `skill` tag in
// `ids`. The predicate targets the joined `st` step (namespace gate + tag match),
// so the join must be present whenever it runs — predicateExtraDims:["skill"]
// forces it (the flow/contrast path). The passed stepAlias is intentionally
// ignored: the predicate lives on the join, not the PROMPT root.
export function skillCombinePredicates(ids = []) {
    const list = (ids || []).filter(Boolean);
    if (!list.length) return null;
    return {
        andExpr: {
            children: [
                { pred: { field: NS_FIELD, op: "OPERATOR_EQ", value: NS } },
                { pred: { field: TAG_FIELD, op: list.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ", value: list.length > 1 ? list : list[0] } },
            ],
        },
    };
}

// ── Facet ────────────────────────────────────────────────────────────
// Distinct skills + firing-prompt counts AND inline sparklines, scoped + ranged,
// in one query. `extraDims:["skill"]` forces the SignalTags join; the namespace
// gate bounds the GROUP BY to skill rows; grouping by (tag, time bucket) gives a
// per-skill total (sum across buckets == overall distinct count, since each
// firing event_id falls in one bucket) plus a densified sparkline.
async function listEntries({ apiScope = [], offset = 0, limit = 100, search = "", sort = "activity", range, steer = null } = {}) {
    const apiRange = toApiRange(range);
    const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: ["skill"], steer });
    if (unsupported.length) return shapeDimensionEntries({ entries: [], total: 0 });
    const nsGate = { pred: { field: NS_FIELD, op: "OPERATOR_EQ", value: NS } };
    const filterParts = [scopeFilter, nsGate].filter(Boolean);
    const bucketSeconds = bucketSecondsForRange(apiRange, SPARK_BUCKETS);
    const { fromMs, toMs } = rangeBoundsMs(apiRange);
    const spec = {
        root,
        timeRange: rangeToTimeRange(apiRange),
        filter: filterParts.length === 1 ? filterParts[0] : { andExpr: { children: filterParts } },
        output: {
            dimensions: [
                { field: TAG_FIELD, outputAlias: "tag" },
                { field: PROMPT_TS, outputAlias: "bucket", bucketSeconds },
            ],
            // DISTINCT firing prompts per skill per bucket — the join fans a prompt
            // out to its tags, so a plain COUNT would over-count.
            metrics: [{ outputAlias: "value", computed: { field: ST_EVENT_ID, op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
    };
    // Preserve a denied or failed read as an error. The sidebar owns the bounded
    // customer-facing copy; converting this to an empty facet makes a policy
    // refusal indistinguishable from a tenant with no recorded skills.
    const rows = rowsToObjects(await runAnalyticsQuery(spec));
    const seriesByTag = densifyBuckets(
        rows.map((r) => ({ id: String(r.tag ?? ""), bucket: r.bucket, value: r.value })),
        { fromMs, toMs, bucketCount: SPARK_BUCKETS, bucketSeconds },
    );
    const totalByTag = new Map();
    for (const r of rows) {
        const tag = String(r.tag ?? "");
        if (tag) totalByTag.set(tag, (totalByTag.get(tag) || 0) + (Number(r.value) || 0));
    }
    const trimmed = String(search || "").trim().toLowerCase();
    let entries = [...totalByTag.keys()].map((tag) => ({
        id: tag,
        label: tag,
        value: totalByTag.get(tag) || 0,
        sparkline: seriesByTag.get(tag) ?? new Array(SPARK_BUCKETS).fill(0),
    }));
    if (trimmed) entries = entries.filter((e) => e.label.toLowerCase().includes(trimmed));
    const sorted = entries.sort(compareDimEntries(sort));
    return shapeDimensionEntries({ entries: sorted.slice(offset, offset + limit), total: sorted.length });
}

async function count(opts = {}) {
    const { entries = [] } = await listEntries({ ...opts, offset: 0, limit: 1000 });
    return entries.length;
}

// ── Adapter export ─────────────────────────────────────────────────
export const skillAdapter = {
    id:     "skill",
    plural: "skills",
    label:  "Skills",
    description: "Skills loaded by agents in each prompt",

    predicate: skillCombinePredicates,
    predicateExtraDims: ["skill"],
    // The predicate is "st.tag is one of ids" under the skill namespace, so
    // grouping by the tag gives every skill's flow links in one read.
    flowGroup: { field: TAG_FIELD, idsForValue: (value, ids) => ids.filter((id) => String(id) === String(value)) },
    listEntries,
    count,
    labelFor: labelForSkill,

    composesWithPromptGraph: true,
    bodyFitsInListProjection: true,
    countBatchable: false,
};
